<template>
    <div>
        <Toast />
        <PrimeDialog class="accept-dialog" v-model:visible="showDialog" :header="setHeader(dialogStatus)">
            <div>
                <p v-if="this.dialogStatus === 'ACCEPT'"
                   class="accept-dialog-question">
                    Czy chcesz zaakceptować planowane wyłączenie?
                </p>
                <p v-if="this.dialogStatus === 'REJECT'" class="accept-dialog-question">
                    Czy chcesz odrzucić planowane wyłączenie?
                </p>
                <p class="accept-dialog-description">
                    (
                    <span v-for="(t, i) in selectedReport.outageReportTimes" :key="i">
                        {{ formatDate(t.outageStart) }} – {{ formatDate(t.outageEnd) }} &nbsp;
                    </span>
                    dodane przez {{ dialogData.createdBy }}
                    )
                </p>
                <div class="accept-dialog-buttons">
                    <EonButton :label="setButtonLabel(dialogStatus)" type="button" @click="acceptOrReject" />
                    <EonOutlinedButton label="Anuluj" type="button" @click="cancel" />
                </div>
                <p v-if="this.dialogStatus === 'ACCEPT'"
                   class="accept-dialog-info">
                    Zaakceptowanie spowoduje publiczne wyświetlenie w Portalu awarii
                </p>
            </div>
        </PrimeDialog>
        <DataTable ref="plannedOutageAcceptTable" class="outages-table" :value="plannedOutages"
                   :lazy="true" :paginator="true" :rowClass="rowClass"
                   v-model:rows="searchParams.page.limit" v-model:first="searchParams.page.offset"
                   :totalRecords="totalRecords" :loading="loading" :search-criteria="searchParams"
                   @page="onPage" @sort="onSort" removableSort v-model:selection="selectedOutages"
                   :rows="10" :rowsPerPageOptions="[5,10,20,50]"
                   paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink
                   LastPageLink CurrentPageReport RowsPerPageDropdown"
                   currentPageReportTemplate="Elementy na stronie" responsiveLayout="scroll">
            <!-- <Column selectionMode="multiple" style="width: 40px" /> -->
            <Column header="Adres">
                <template #body="slotProps">
                    <ul v-if="slotProps.data.addresses.length <= 10" class="outage-address-list">
                        <li v-for="address in slotProps.data.addresses" :key="address.address">
                            <b>{{ address.nonFormattedAddress }}</b> - {{ address.street }}
                            {{ address.wesolaDistrict ? "(W)" : "" }}
                            {{ address.streetNumber }}{{ ", " + address.postcode }}
                        </li>
                    </ul>
                    <ul v-else class="outage-address-list">
                        <li v-for="index in 10" :key="index">
                            <b>{{ slotProps.data.addresses[index].nonFormattedAddress }}</b> -
                            {{ slotProps.data.addresses[index].street }}
                            {{ slotProps.data.addresses[index].wesolaDistrict ? "(W)" : "" }}
                            {{ slotProps.data.addresses[index].streetNumber }}
                            {{ ", " + slotProps.data.addresses[index].postcode }}
                        </li>
                        <b>...</b>
                    </ul>
                </template>
            </Column>
            <Column header="Czas rozpoczęcia" :sortable="true" field="outageStart">
                <template #body="slotProps">
                    <template v-if="slotProps.data.outageReportTimes?.length">
                        <div v-for="(t, i) in slotProps.data.outageReportTimes" :key="i">
                            {{ formatDate(t.outageStart) }}
                        </div>
                    </template>
                    <span v-else>-</span>
                </template>
            </Column>
            <Column header="Czas zakończenia" :sortable="true" field="outageEnd">
                <template #body="slotProps">
                    <template v-if="slotProps.data.outageReportTimes?.length">
                        <div v-for="(t, i) in slotProps.data.outageReportTimes" :key="i">
                            {{ formatDate(t.outageEnd) }}
                        </div>
                    </template>
                    <span v-else>-</span>
                </template>
            </Column>
            <Column field="createdBy.fullName" header="Dodane przez" :sortable="true" />
            <Column field="voltageLevel" header="Napięcie" :sortable="true" style="text-align: center;">
                <template #body="slotProps">
                    {{ slotProps.data.voltageLevel === "LV" ? "nN" : "" }}
                    {{ slotProps.data.voltageLevel === "MV" ? "SN" : "" }}
                    {{ slotProps.data.voltageLevel === "HV" ? "WN" : "" }}
                </template>
            </Column>
            <Column field="taskNumber" header="Nr SAP/CallNet" :sortable="true" style="width: 140px;" />
            <Column field="acceptStatus" header="Status akceptacji" :sortable="true" style="width: 160px;">
                <template #body="slotProps">
                    <span v-if="slotProps.data.acceptStatus.value === 'ACCEPTED'" style="color: green;">
                        {{getStatusLabel(slotProps.data.acceptStatus.value)}}
                    </span>
                    <span v-else-if="slotProps.data.acceptStatus.value === 'REJECTED'" style="color: red;">
                        {{getStatusLabel(slotProps.data.acceptStatus.value)}}
                    </span>
                    <span v-else>
                        {{getStatusLabel(slotProps.data.acceptStatus.value)}}
                    </span>
                </template>

            </Column>
            <Column header="Zaakceptuj / Odrzuć" style="width: 180px;">
                <template #body="slotProps">
                    <div class="button-container">
                        <PrimeButton class="accept-button" @click="acceptPlannedOutage(slotProps.data)"
                                     :disabled="slotProps.data.acceptStatus.value === 'ACCEPTED'">
                            <img alt="przycisk akceptacji" class="accept-button-tick"
                                 src="@/assets/tick.svg" />
                        </PrimeButton>
                        <PrimeButton class="accept-button" @click="rejectPlannedOutage(slotProps.data)"
                                     :disabled="slotProps.data.acceptStatus.value === 'REJECTED'">
                            <img alt="przycisk odrzucenia" class="accept-button-cross"
                                 src="@/assets/cross.svg" />
                        </PrimeButton>
                    </div>
                </template>
            </Column>
            <Column style="width: 60px">
                <template #body="slotProps">
                    <div class="button-column">
                        <router-link :to="{name: 'plannedOutageAcceptShow', params: {'id': slotProps.data.id}}">
                            <PrimeButton class="outages-button outages-button-show" />
                        </router-link>
                    </div>
                </template>
            </Column>
            <template #loading>
                Ładowanie planowanych wyłączeń. Proszę czekać.
            </template>
            <template #empty>
                {{ $t('message.other.emptyTable') }}
            </template>
        </DataTable>
    </div>
</template>

<script>
    import DataTable from "primevue/datatable";
    import Column from "primevue/column";
    import EonCheckbox from "@/components/form/EonCheckbox";
    import PrimeButton from "primevue/button";
    import PrimeDialog from "primevue/dialog";
    import EonButton from "@/components/form/EonButton";
    import EonOutlinedButton from "@/components/form/EonOutlinedButton";
    import EonDeleteOrRefreshPopup from "@/components/form/EonDeleteOrRefreshPopup";
    import Toast from "primevue/toast";
    import {SearchCriteria} from "@/utils/SearchCriteria";
    import {TimeUtils} from "@/utils/TimeUtils";
    import {
        deleteManyPlannedOutage as deleteSelected,
        searchPlannedOutageNoDraft as searchPlannedOutage,
        searchPlannedOutageNoDraftCount as searchPlannedOutageCount,
        changeAcceptStatus as acceptOutageReport,
        getAcceptStatuses,
    } from "@/swagger/vue-api-client";

    export default {
        name: "PlannedOutageAcceptTable",

        components: {
            DataTable,
            Column,
            EonCheckbox,
            PrimeButton,
            PrimeDialog,
            EonButton,
            EonOutlinedButton,
            EonDeleteOrRefreshPopup,
            Toast,
        },

        props: {
            searchCriteria: {
                type: Object,
            },
        },

        emits: ["update:searchCriteria"],

        data() {
            return {
                loading: false,
                totalRecords: 0,
                plannedOutages: [],
                showDialog: false,
                dialogStatus: "",
                selectedOutages: null,
                dialogData: {
                    outageStart: "",
                    outageEnd: "",
                    createdBy: "",
                },
                selectedReport: null,
                plannedOutageIndex: null,
                statusLabels: {},
            };
        },

        computed: {
            searchParams: {
                get() {
                    return this.searchCriteria;
                },
                set(value) {
                    this.$emit("update:searchCriteria", value);
                },
            },
            getStatusLabel() {
                return (statusValue) => this.statusLabels[statusValue];
            },
        },

        beforeMount() {
            getAcceptStatuses().then((response) => {
                if (Array.isArray(response.data)) {
                    this.statusLabels = response.data.reduce((map, status) => {
                        map[status.value] = status.label;
                        return map;
                    }, {});
                } else {
                    this.statusLabels = response.data;
                }
            });
        },

        mounted() {
            this.updateTotalRecords();
            this.onPage(this.getFirstPage());
        },

        methods: {
            search() {
                this.onPage(this.getFirstPage());
                this.updateTotalRecords();
            },

            onPage(event) {
                this.loading = true;

                this.searchParams.page.offset = event.first;
                this.searchParams.page.limit = event.rows;
                this.searchParams.page.sortField = event.sortField;
                this.searchParams.page.sortOrder = event.sortOrder;

                if (this.searchParams.page.sortField === undefined) this.searchParams.page.sortField = "outageStart";
                if (this.searchParams.page.sortOrder === undefined) this.searchParams.page.sortOrder = -1;

                searchPlannedOutage({body: TimeUtils.adjustRequestBody(this.searchParams)})
                    .then((response) => {
                        this.plannedOutages = response.data;
                        this.plannedOutages.forEach((report) => {
                            report.outageStart = new Date(report.outageStart)
                                .toLocaleString("pl-PL", TimeUtils.options);
                            report.outageEnd = new Date(report.outageEnd)
                                .toLocaleString("pl-PL", TimeUtils.options);
                        });
                        this.loading = false;
                    });

                SearchCriteria.updateUrl(this);
            },

            onSort(event) {
                const field = event.sortField;
                const order = event.sortOrder;

                this.searchParams.page.sortField = field;
                this.searchParams.page.sortOrder = order;
                this.onPage({
                    first: 0,
                    rows: this.searchParams.page.limit,
                    sortField: field,
                    sortOrder: order,
                });
            },

            formatDate(date) {
                if (!date) return "-";
                return new Date(date).toLocaleTimeString("pl-PL", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                });
            },

            compare(a, b, order) {
                if (a == null && b == null) return 0;
                if (a == null) return 1;
                if (b == null) return -1;
                return order * (a - b);
            },

            rowClass(rowData) {
                console.log(rowData);
                if (rowData.deleted) return "deleted-row";
                if (rowData.acceptStatus.value === "TO_ACCEPT") return "accepted-row";
                return null;
            },

            updateTotalRecords() {
                searchPlannedOutageCount({body: this.searchParams}).then((response) => {
                    this.totalRecords = response.data;
                });
            },

            getFirstPage() {
                return {
                    first: this.searchParams.page.offset,
                    rows: this.searchParams.page.limit,
                };
            },

            async deleteSelectedOutages() {
                if (this.selectedOutages != null && this.selectedOutages.length > 0) {
                    const selectedIds = this.selectedOutages.map((outage) => outage.id);
                    deleteSelected({body: {ids: selectedIds}});
                    this.$toast.removeAllGroups();
                    this.$toast.add({
                        severity: "success",
                        summary: "Pomyślnie usunięto zaznaczone wyłączenia",
                        life: 2500,
                    });
                    // eslint-disable-next-line no-promise-executor-return
                    await new Promise((r) => setTimeout(r, 300));
                    this.search();
                } else {
                    this.$toast.add({
                        severity: "error",
                        summary: "Nie zaznaczono żadnych wyłączeń",
                        life: 2500,
                    });
                }
            },

            acceptOutage(report) {
                const index = this.plannedOutages.indexOf(report);
                this.plannedOutages[index].acceptStatus = "ACCEPTED";
            },

            rejectOutage(report) {
                const index = this.plannedOutages.indexOf(report);
                this.plannedOutages[index].acceptStatus = "REJECTED";
            },

            acceptOrReject() {
                if (this.dialogStatus === "ACCEPT") {
                    this.plannedOutages[this.plannedOutageIndex].acceptStatus.value = "ACCEPTED";
                    acceptOutageReport({
                        body: {
                            id: this.plannedOutages[this.plannedOutageIndex].id, acceptStatus: "ACCEPTED",
                        },
                    });
                    this.showDialog = false;
                }
                if (this.dialogStatus === "REJECT") {
                    this.plannedOutages[this.plannedOutageIndex].acceptStatus.value = "REJECTED";
                    acceptOutageReport({
                        body: {
                            id: this.plannedOutages[this.plannedOutageIndex].id, acceptStatus: "REJECTED",
                        },
                    });
                    this.showDialog = false;
                }
            },

            acceptPlannedOutage(data) {
                this.dialogStatus = "ACCEPT";
                this.dialogData.createdBy = data.createdBy.fullName;
                this.selectedReport = data;
                this.plannedOutageIndex = this.plannedOutages.indexOf(data);
                this.showDialog = true;
            },

            rejectPlannedOutage(data) {
                this.dialogStatus = "REJECT";
                this.dialogData.createdBy = data.createdBy.fullName;
                this.selectedReport = data;
                this.plannedOutageIndex = this.plannedOutages.indexOf(data);
                this.showDialog = true;
            },

            setHeader() {
                if (this.dialogStatus === "REJECT") return "Odrzuć planowane wyłączenie";
                if (this.dialogStatus === "ACCEPT") return "Zaakceptuj planowane wyłączenie";
                return "";
            },

            setButtonLabel() {
                if (this.dialogStatus === "REJECT") return "Odrzuć";
                if (this.dialogStatus === "ACCEPT") return "Akceptuj";
                return "";
            },

            cancel() {
                this.showDialog = false;
                this.dialogData.createdBy = "";
                this.dialogStatus = "";
            },
        },
    };
</script>

<style lang="less" scoped>
@import "../../../components/assets/less/datatable.less";
@import "../../assets/less/eon-table.less";
.button-column {
    display: flex;
    justify-content: center;
    gap: 4px;
}
.accept-button {
    background-color: transparent;
    border: none;
}
.outages-button {
    background-color: transparent;
    border: none;
    width: 24px;
    height: 24px;
    &-show {
        background-image: url("@/assets/show.svg");
    }
    &-edit {
        background-image: url("@/assets/edit_no_line.svg");
    }
    &-trash {
        background-image: url("@/assets/trash_bin.svg");
    }
    &-print {
        background-image: url("@/assets/printer.svg");
    }
}
.outage-address-list {
    list-style: none;
    padding: 0;
    margin: 0;
}
.button-container {
    display: flex;
    // justify-content: center;
    gap: 12px;
}
.accept-button-tick, .accept-button-cross {
    width: 32px;
    height: 32px;
    background-color: transparent;
}
</style>

<style lang="less">
@import "../../../assets/theme/variable.less";
.accepted-row {
    background-color: #E1EDED !important;
}
.accept-dialog {
    background-color: @color-white;
    width: 960px;
    height: 400px;
    border-radius: 16px;
    position: relative;
    .p-dialog-header {
        background-color: @color-grey-light;
        border-top-left-radius: 16px;
        border-top-right-radius: 16px;
        height: 48px;
        .p-dialog-title {
            color: @tint-grey-dark-01;
            font-weight: 400;
            padding-left: 1rem;
        }
        &-icons {
            padding-right: 1rem;
        }
    }
    .p-dialog-content {
        display: flex;
        justify-content: center;
        p {
            text-align: center;
            &.accept-dialog-question {
                font-size: 2.25rem;
                margin-bottom: 0;
            }
            &.accept-dialog-description {
                font-size: 1.125rem;
                margin-top: 12px;
                margin-bottom: 48px;
            }
        }
        .accept-dialog-buttons {
            display: flex;
            justify-content: center;
            gap: 80px;
        }
    }
    &-info {
        position: absolute;
        bottom: 0;
        left: 50%;
        transform: translateX(-50%);
        color: @color-red;
    }
}
</style>
